import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Sparkles, Trash2, Save } from "lucide-react";
import api from "@/lib/api";
import { Btn, Card, Head, Inp, Pill, Empty, errMsg, download, useMeta, fmtDate, ENTITY_OPTS, DC } from "@/components/datacenter/ui";

export function TemplatesPage() {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const analyze = async () => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const r = await api.post(`${DC}/ai/template-from-file`, fd); setRes(r.data); setName(r.data.suggested_name); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try { await api.post(`${DC}/mapping-templates`, { name, entity: res.entity, mapping: res.mapping, source_headers: res.headers }); toast.success("Template saved — future uploads with these columns map automatically"); setRes(null); setFile(null); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-5xl space-y-5" data-testid="templates-page">
      <Head title="Import templates" sub="Download a ready-made CSV for bulk onboarding, or let AI build a mapping template from a spreadsheet you already have." />
      <div className="grid gap-4 sm:grid-cols-3">
        {ENTITY_OPTS.map(([k, l]) => (
          <Card key={k} tid={`tpl-card-${k}`}>
            <div className="font-display text-lg font-semibold">{l}</div>
            <div className="mt-3 flex flex-col gap-2">
              <Btn kind="ghost" onClick={() => download("get", `${DC}/templates/download/${k}?version=simple`, null, `${k}_simple.csv`)} tid={`tpl-simple-${k}`}><Download className="h-4 w-4" /> Simple template</Btn>
              <Btn kind="ghost" onClick={() => download("get", `${DC}/templates/download/${k}?version=complete`, null, `${k}_complete.csv`)} tid={`tpl-complete-${k}`}><Download className="h-4 w-4" /> Complete template</Btn>
            </div>
          </Card>
        ))}
      </div>
      <Card tid="tpl-ai-creator">
        <div className="font-display text-lg font-semibold inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#d97757]" /> Create import template from this file</div>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <input type="file" accept=".csv,.tsv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="tpl-file" className="text-sm" />
          <Btn onClick={analyze} busy={busy} disabled={!file} tid="tpl-analyze">Analyze columns</Btn>
        </div>
        {res && (
          <div className="mt-4" data-testid="tpl-result">
            <div className="text-sm">Looks like <b>{res.entity}</b> · {res.rows} rows</div>
            <table className="mt-2 w-full text-sm"><tbody>{res.mapping.map((m) => <tr key={m.column} className="border-t border-slate-100"><td className="py-1">{m.column}</td><td>{m.field || <span className="text-[#252A34]/40">not imported</span>}</td><td>{m.field && <Pill s={m.confidence >= 85 ? "ready" : "warning"}>{m.confidence}%</Pill>}</td></tr>)}</tbody></table>
            <div className="mt-3 flex gap-2"><Inp value={name} onChange={(e) => setName(e.target.value)} className="flex-1" data-testid="tpl-name" /><Btn onClick={save} tid="tpl-save"><Save className="h-4 w-4" /> Save template</Btn></div>
          </div>
        )}
      </Card>
    </div>
  );
}

export function MappingsPage() {
  const meta = useMeta();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`${DC}/mapping-templates`).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put(`${DC}/mapping-templates/${edit.id}`, edit); toast.success("Template updated"); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => { await api.delete(`${DC}/mapping-templates/${t.id}`); toast.success("Template deleted"); load(); };
  return (
    <div className="max-w-5xl space-y-4" data-testid="mappings-page">
      <Head title="Field mapping templates" sub="Saved column mappings. When a new upload has similar columns, its template is applied automatically." />
      {items === null ? "Loading…" : items.length === 0 ? <Empty tid="mappings-empty">No saved mappings yet — save one from the mapping step of any import.</Empty> : items.map((t) => (
        <Card key={t.id} tid={`mapping-${t.id}`}>
          <div className="flex justify-between items-center gap-2">
            <div><div className="font-semibold">{t.name}</div><div className="text-xs text-[#252A34]/60">{t.entity} · {t.mapping.filter((m) => m.field).length}/{t.mapping.length} columns mapped · {fmtDate(t.created_at)}</div></div>
            <div className="flex gap-2"><Btn kind="ghost" onClick={() => setEdit(edit?.id === t.id ? null : JSON.parse(JSON.stringify(t)))} tid={`mapping-edit-${t.id}`}>Edit</Btn><Btn kind="ghost" onClick={() => del(t)} tid={`mapping-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Btn></div>
          </div>
          {edit?.id === t.id && meta?.entities && (
            <div className="mt-3 space-y-1.5">
              <Inp value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full" data-testid="mapping-edit-name" />
              {edit.mapping.map((m, i) => (
                <div key={m.column} className="flex items-center gap-2 text-sm"><span className="w-48 truncate">{m.column}</span>
                  <select value={m.field || ""} onChange={(e) => { const mm = [...edit.mapping]; mm[i] = { ...m, field: e.target.value || null }; setEdit({ ...edit, mapping: mm }); }} className="rounded-lg border border-slate-200 px-2 py-1 flex-1" data-testid={`mapping-edit-field-${i}`}>
                    <option value="">— Don't import —</option>{meta.entities[t.entity].fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select></div>
              ))}
              <div className="flex justify-end"><Btn onClick={save} tid="mapping-edit-save">Save</Btn></div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function Table({ cols, rows, tid }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid={tid}>
      <table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-[#252A34]/50 bg-slate-50">{cols.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}</tr></thead>
        <tbody>{rows.length === 0 ? <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-[#252A34]/50">Nothing here yet.</td></tr> : rows}</tbody></table>
    </div>
  );
}

export function HistoryPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`${DC}/imports`).then((r) => setItems(r.data.items)); }, []);
  return (
    <div data-testid="history-page"><Head title="Import history" sub="Open an import to see row-level results, download its error report or roll it back." />
      <Table tid="history-table" cols={["Import ID", "File", "Entity", "Uploaded by", "Date", "Rows", "Created", "Updated", "Skipped", "Errors", "Duplicates", "Status"]} rows={items.map((j) => (
        <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${j.id}`}>
          <td className="px-3 py-2"><Link to={`${DC}/history/${j.id}`} className="text-[#165a71] underline">{j.id.slice(-8)}</Link></td><td className="px-3 max-w-[180px] truncate">{j.file?.name}</td><td className="px-3">{j.entity}</td>
          <td className="px-3">{j.created_by_name}</td><td className="px-3 whitespace-nowrap">{fmtDate(j.created_at)}</td><td className="px-3">{j.file?.rows}</td><td className="px-3">{j.counts?.created ?? "—"}</td>
          <td className="px-3">{j.counts ? (j.counts.updated || 0) + (j.counts.merged || 0) : "—"}</td><td className="px-3">{j.counts?.skipped ?? "—"}</td><td className="px-3">{j.counts?.errors ?? "—"}</td><td className="px-3">{j.summary?.duplicate ?? 0}</td>
          <td className="px-3"><Pill s={j.stage === "completed" ? "created" : j.stage === "failed" ? "error" : j.stage === "rolled_back" ? "skip" : "warning"}>{(j.stage || "").replace("_", " ")}</Pill></td>
        </tr>))} />
    </div>
  );
}

export function ExportHistoryPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`${DC}/exports`).then((r) => setItems(r.data.items)); }, []);
  return (
    <div data-testid="export-history-page"><Head title="Export history" sub="Every export is logged with who ran it and what it contained." />
      <Table tid="export-history-table" cols={["Date", "By", "Entity", "Related", "Rows", "Fields", "Filters", "File"]} rows={items.map((x) => (
        <tr key={x.id} className="border-t border-slate-100"><td className="px-3 py-2 whitespace-nowrap">{fmtDate(x.created_at)}</td><td className="px-3">{x.created_by_name}</td><td className="px-3">{x.entity}</td><td className="px-3">{x.related || "—"}</td><td className="px-3">{x.rows}</td><td className="px-3">{x.fields?.length || "all"}</td>
          <td className="px-3 text-xs max-w-[240px] truncate">{Object.entries(x.filters || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || (x.selected_ids ? `${x.selected_ids} selected` : "all")}</td><td className="px-3">{x.file}</td></tr>))} />
    </div>
  );
}

export function ErrorsPage() {
  const [items, setItems] = useState([]);
  const [level, setLevel] = useState("");
  useEffect(() => { api.get(`${DC}/errors`, { params: { level: level || undefined } }).then((r) => setItems(r.data.items)); }, [level]);
  return (
    <div data-testid="errors-page"><Head title="Error reports" sub="Problems found across imports. Errors block a row; warnings drop just the bad value.">
      {["", "error", "warning"].map((l) => <Btn key={l || "all"} kind={level === l ? "primary" : "ghost"} onClick={() => setLevel(l)} tid={`errors-filter-${l || "all"}`}>{l || "All"}</Btn>)}</Head>
      <Table tid="errors-table" cols={["File", "Entity", "Row", "Field", "Original value", "Problem", "Suggested fix", ""]} rows={items.map((e) => (
        <tr key={e.id} className="border-t border-slate-100"><td className="px-3 py-2 max-w-[160px] truncate">{e.file}</td><td className="px-3">{e.entity}</td><td className="px-3">{e.row}</td><td className="px-3">{e.field || "—"}</td><td className="px-3 max-w-[160px] truncate">{String(e.original ?? "")}</td>
          <td className="px-3"><Pill s={e.level === "error" ? "invalid" : "warning"}>{e.level}</Pill> {e.message}</td><td className="px-3 text-xs">{e.suggestion || ""}</td><td className="px-3"><Link className="text-[#165a71] underline text-xs" to={`${DC}/history/${e.job_id}`}>Open</Link></td></tr>))} />
    </div>
  );
}

export function AuditPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`${DC}/audit`).then((r) => setItems(r.data.items)); }, []);
  return (
    <div data-testid="audit-page"><Head title="Data Center audit log" sub="Uploads, mappings, AI changes, imports, merges, exports and rollbacks." />
      <Table tid="audit-table" cols={["Date", "Action", "Entity", "File", "Details"]} rows={items.map((a) => (
        <tr key={a.id} className="border-t border-slate-100"><td className="px-3 py-2 whitespace-nowrap">{fmtDate(a.created_at)}</td><td className="px-3">{a.action}</td><td className="px-3">{a.entity || "—"}</td><td className="px-3">{a.file || "—"}</td>
          <td className="px-3 text-xs max-w-[420px] truncate">{JSON.stringify(a.details)}</td></tr>))} />
    </div>
  );
}
